import { useProfileGalleriesStore } from '../../features/galleries/store/useProfileGalleriesStore';
import { useProfileDatasetsStore } from '../../features/datasets/store/useProfileDatasetsStore';
import { useProfileBoardsStore } from '../../features/boards/store/useProfileBoardsStore';
import type { LanguageCode } from '../../shared/types/LanguageType';
import type { AccentType } from '../../shared/types/AccentType';
import type { LayoutType } from '../../shared/types/LayoutType';
import type { ResolvedTheme, ThemePreference } from '../../shared/types/ThemeType';
import type { FolderType, RootFolderSettings, UserResponse } from './types';
import { useAuthStore } from './useAuthStore';
import { useUiStore } from './useUiStore';
import { useWorkspaceStore } from './useWorkspaceStore';
import {
  getCurrentFolderFromUser,
  getFolderByType,
  getFolderUiSettings,
  resolveFolderType,
  resolveWorkspaceAfterUserUpdate,
} from './workspaceLogic';

export const appActions = {
  setUser: (payload: UserResponse) => {
    const authStore = useAuthStore.getState();
    const workspaceStore = useWorkspaceStore.getState();
    const uiStore = useUiStore.getState();

    const nextUser = authStore.mergeUser(payload);
    const workspace = resolveWorkspaceAfterUserUpdate(
      nextUser.rootFolders,
      nextUser.sharedFolders,
      workspaceStore.currentFolderId,
      workspaceStore.folderType
    );

    workspaceStore.setWorkspace(workspace);

    const folderSettings = getFolderUiSettings(
      nextUser.rootFolders,
      nextUser.sharedFolders,
      workspace.currentFolderId,
      workspace.folderType
    );

    if (folderSettings) {
      uiStore.applyFolderSettings(folderSettings);
    }
  },

  setCurrentFolder: (folderId: string, folderType?: FolderType) => {
    const authStore = useAuthStore.getState();
    const workspaceStore = useWorkspaceStore.getState();
    const uiStore = useUiStore.getState();

    const type = resolveFolderType(authStore.user, folderId, folderType);
    if (!type) {
      return;
    }

    const folder = getFolderByType(authStore.user, folderId, type);
    if (!folder) {
      return;
    }

    workspaceStore.setWorkspace({
      currentFolderId: folderId,
      folderType: type,
      role: type === 'own'
        ? 'owner'
        : ('role' in folder ? folder.role : 'viewer'),
    });

    uiStore.applyFolderSettings({
      mode: folder.settings.mode,
      currentLang: folder.settings.currentLang as LanguageCode,
      isInitialized: folder.settings.isInitialized,
      accent: folder.settings.accent,
      themeSchedule: folder.settings.themeSchedule,
    });
  },

  getCurrentFolder: () => {
    const { user } = useAuthStore.getState();
    const { currentFolderId, folderType } = useWorkspaceStore.getState();

    return getCurrentFolderFromUser(user, currentFolderId, folderType);
  },

  getCurrentFolderSettings: (): RootFolderSettings | undefined => {
    return appActions.getCurrentFolder()?.settings;
  },

  setLang: (lang: LanguageCode) => {
    useUiStore.getState().setLang(lang);
  },

  initializeLang: (lang: LanguageCode) => {
    const hasUser = Boolean(useAuthStore.getState().user.id);
    useUiStore.getState().initializeLang(lang, hasUser);
  },

  setIsAuto: (payload: boolean) => {
    useUiStore.getState().setIsAuto(payload);
  },

  setMode: (payload: ResolvedTheme) => {
    useUiStore.getState().setMode(payload);
  },

  setThemePreference: (payload: ThemePreference) => {
    useUiStore.getState().setThemePreference(payload);
  },

  setAccent: (accent: AccentType) => {
    useUiStore.getState().setAccent(accent);
  },

  setLayout: (payload: LayoutType) => {
    useUiStore.getState().setLayout(payload);
  },

  setPreviousLayout: (payload: LayoutType | '') => {
    useUiStore.getState().setPreviousLayout(payload);
  },

  reset: () => {
    useAuthStore.getState().resetUser();
    useWorkspaceStore.getState().resetWorkspace();
    useUiStore.getState().resetUi();
    useProfileGalleriesStore.getState().resetProfileGalleries();
    useProfileDatasetsStore.getState().resetProfileDatasets();
    useProfileBoardsStore.getState().resetProfileBoards();
  },

  syncWorkspaceUi: () => {
    const { user } = useAuthStore.getState();
    if (!user?.id) {
      return;
    }

    const { currentFolderId, folderType } = useWorkspaceStore.getState();
    const folderSettings = getFolderUiSettings(
      user.rootFolders,
      user.sharedFolders,
      currentFolderId,
      folderType
    );

    if (folderSettings) {
      useUiStore.getState().applyFolderSettings(folderSettings);
    }
  },
};
